import { gql, useQuery } from '@apollo/client';

import type {
  GetAccountRemittanceRecords,
  GetAccountRemittanceRecordsVariables,
} from './__generated__/getAccountRemittanceRecords_gql';

type GetAccountRemittanceRecords_getAccountRemittanceRecords_records_by_account_id_records =
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<GetAccountRemittanceRecords>['getAccountRemittanceRecords']
      >['records_by_account_id'][number]
    >['records']
  >[number];
/**
 * Fetches remittance records for one or more accounts in a deal from loan-service
 * (getAccountRemittanceRecords).
 */
export const GET_ACCOUNT_REMITTANCE_RECORDS = gql`
  query GetAccountRemittanceRecords($dealId: ID!, $accountIds: [ID!]!) {
    getAccountRemittanceRecords(input: { deal_id: $dealId, account_ids: $accountIds }) {
      records_by_account_id {
        account_id
        records {
          payment_month
          starting_balance_cents
          payment_cents
          principal_cents
          interest_cents
          servicing_cents
          prepayments_cents
          defaults_cents
          losses_cents
          current_balance_cents
        }
      }
    }
  }
`;

/** Record shape for the monthly table; matches generated query result. */
export type AccountRemittanceRecordRow =
  GetAccountRemittanceRecords_getAccountRemittanceRecords_records_by_account_id_records;

/** Fetches remittance records for one account in a deal via getAccountRemittanceRecords (loan-service). */
export function useGetAccountRemittanceRecords(
  dealId: string | undefined,
  accountId: string | undefined,
) {
  const skip = !dealId || !accountId;
  const { data, loading, error } = useQuery<
    GetAccountRemittanceRecords,
    GetAccountRemittanceRecordsVariables
  >(GET_ACCOUNT_REMITTANCE_RECORDS, {
    variables: {
      dealId: dealId ?? '',
      accountIds: accountId ? [accountId] : [],
    },
    skip,
    fetchPolicy: 'network-only',
  });

  const recordsByAccountId = data?.getAccountRemittanceRecords?.records_by_account_id ?? [];
  const accountBlock = recordsByAccountId.find((block) => block.account_id === accountId);
  const entities: AccountRemittanceRecordRow[] = accountBlock?.records ?? [];

  return { data: { entities }, loading, error };
}
